import { createDocument, readDocumentById, updateDocumentById, deleteDocument } from '../../configurations/DataBase/crud';
import { User, UserDoc, LoginCredentials } from './types';
import { v4 as uuidv4 } from 'uuid';
import { basicGet, basicPost } from '../../configurations/Axios/GenericMethods';
import { visitaxClient } from '../../configurations/Axios/Clients';
import { LoginUrl, ScheduleControlDevices } from '../../configurations/Axios/urls';
import { userMapping } from '../../configurations/Mappings/Mappings';
const USER_DOC_ID = 1; // ID estático para el documento de usuario

const findErrorMessage = (value: any): string | null => {
  if (!value) return null;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findErrorMessage(item);
      if (found) return found;
    }
    return null;
  }

  if (typeof value === 'object') {
    const directKeys = ['messageerror', 'messageError', 'mensajeerror', 'mensajeError'];

    for (const key of directKeys) {
      const candidate = value?.[key];
      if (typeof candidate === 'string' && candidate.trim().length > 0) {
        return candidate.trim();
      }
    }

    for (const key of Object.keys(value)) {
      const found = findErrorMessage(value[key]);
      if (found) return found;
    }
  }

  return null;
};

/**
 * Authenticates a user against the Visitax backend.
 *
 * @param credentials - Object containing the username and password.
 * @returns The authenticated {@link User}.
 */
export const authenticateUser = async (credentials: { user: string; password: string }): Promise<User> => {
  try {
    const deviceId = await getDeviceId();
    const user = await loginUser(credentials, deviceId);
    const usermapped = userMapping(user);
    const assignedTpv = await fetchAdvisorTPV(usermapped.id, usermapped.token);
    const userWithDevice = assignedTpv ? { ...usermapped, tpv: assignedTpv } : usermapped;
    await saveUser(userWithDevice);
    return userWithDevice;
  } catch (err) {
    const axiosError = err as any;
    const endpointMessage =
      findErrorMessage(axiosError?.response?.data) ||
      findErrorMessage(axiosError?.response?.data?.data) ||
      findErrorMessage(axiosError?.response?.data?.ils) ||
      axiosError?.response?.data?.message ||
      axiosError?.response?.data?.error ||
      axiosError?.message;

    throw new Error(endpointMessage || 'Login failed');
  }
};

const loginUser = async (credentials: LoginCredentials, deviceId: string): Promise<User> => {
  return new Promise((resolve, reject) => {
    basicPost(visitaxClient, LoginUrl, { ...credentials, deviceId }, (response) => {
      if (response instanceof Error) {
        reject(response);
      } else {
        resolve(response.data.data);
      }
    });
  });
};

/**
 * Persists a user document in the local database.
 *
 * @param user - The user information to store.
 */
export const saveUser = async (user: User): Promise<void> => {
  const userdoc: UserDoc = { id: USER_DOC_ID, user };
  try {
    const existingUser = await readDocumentById(USER_DOC_ID);
    if (existingUser) {
      await updateDocumentById(USER_DOC_ID, userdoc);
    }
  } catch (err) {
    if (err instanceof Error) {
      await createDocument(userdoc);
    } else {
      throw err;
    }
  }
};

/**
 * Retrieves the stored user document.
 *
 * @returns The user document or `null` when no user is saved.
 */
export const readUser = async (): Promise<UserDoc | null> => {
  try {
    const user = await readDocumentById(USER_DOC_ID);
    return (user as UserDoc) || null;
  } catch (err) {
    return null;
  }
};

/**
 * Removes any persisted user information.
 */
export const logoutUser = async (): Promise<void> => {
  try {
    await deleteDocument(USER_DOC_ID);
  } catch (err) {
    throw err;
  }
};

/**
 * Returns a unique device identifier, generating one when necessary.
 *
 * @returns Device identifier string.
 */
export const getDeviceId = async (): Promise<string> => {
  const docId = 'deviceIdDoc';
  try {
    const deviceId = localStorage.getItem(docId);
    if (deviceId) {
      return deviceId;
    }

    const newDeviceId = uuidv4();
    localStorage.setItem(docId, newDeviceId);
    return newDeviceId;
  } catch (err) {
    throw err;
  }
};

/**
 * Retrieve the TPV device assigned to the adviser, if any.
 *
 * @param advisorId Adviser identifier.
 * @param token Authentication token used for the request.
 * @returns The serial number of the assigned TPV or `null` when none exists.
 */
export const fetchAdvisorTPV = async (
  advisorId: string,
  token: string
): Promise<string | null> => {
  if (!advisorId || !token) return null;

  return new Promise((resolve) => {
    basicGet(
      visitaxClient,
      `${ScheduleControlDevices}/${advisorId}`,
      (response) => {
        if (response instanceof Error) {
          resolve(null);
          return;
        }

        const { status, data } = response as any;
        if (status !== 200) {
          resolve(null);
          return;
        }

        const rawDevice = data?.data;

        const extractSerial = (device: any): string | null => {
          if (!device) return null;
          if (Array.isArray(device)) {
            for (const item of device) {
              const serial = extractSerial(item);
              if (serial) return serial;
            }
            return null;
          }
          if (typeof device === 'string') {
            return device;
          }
          if (device?.serialNumber) {
            return device.serialNumber;
          }
          if (device?.device?.serialNumber) {
            return device.device.serialNumber;
          }
          if (device?.device_key) {
            return device.device_key;
          }
          return null;
        };

        resolve(extractSerial(rawDevice));
      },
      token
    );
  });
};
